//src/components/About.js
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axiosInstance from '../axios';
import '../styles/about.css';
import Skeleton from '../ui/Skeleton';

const AboutSkeleton = () => {
    return (
        <section className="about-section">
            <div className="container">
                <div className="about-hero-card">
                    <div className="row align-items-center g-4">
                        <div className="col-lg-6">
                            <Skeleton className="about-skeleton-image" />
                        </div>

                        <div className="col-lg-6">
                            <Skeleton className="about-skeleton-kicker mb-3" />
                            <Skeleton className="about-skeleton-title mb-3" />
                            <Skeleton className="about-skeleton-line mb-2" />
                            <Skeleton className="about-skeleton-line mb-2" />
                            <Skeleton className="about-skeleton-line short mb-2" />
                            <Skeleton className="about-skeleton-line mb-2" />
                            <Skeleton className="about-skeleton-line long mb-2" />
                            <Skeleton className="about-skeleton-line long mb-2" />
                            <Skeleton className="about-skeleton-line medium mb-4" />

                            <div className="d-flex flex-wrap gap-2">
                                <Skeleton className="about-skeleton-button" />
                                <Skeleton className="about-skeleton-button secondary" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const About = () => {
    const [about, setAbout] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const fallbackImage = '/about-image.jpg';

    const getImageUrl = (item) => {
        if (!item) return fallbackImage;

        if (item.image_url) {
            return item.image_url;
        }

        if (item.image) {
            const base = process.env.REACT_APP_STORAGE_URL || 'https://uat-api.urbania.id';

            return `${base}/storage/${item.image}`;
        }

        return fallbackImage;
    };

    useEffect(() => {
        const fetchAbout = async () => {
            try {
                setLoading(true);
                setError('');

                const response = await axiosInstance.get('/abouts');

                const firstItem = response.data?.data?.[0] || null;

                setAbout(firstItem);
            } catch (err) {
                console.error('Error fetching about:', err);

                setError('Failed to load about data. Please try again later.');

                setAbout(null);
            } finally {
                setLoading(false);
            }
        };

        fetchAbout();
    }, []);

    const aboutImage = useMemo(() => {
        const url = getImageUrl(about);

        console.log('About Image URL:', url);

        return url;
    }, [about]);

    const seoDescription = useMemo(() => {
        const fallback =
            'Urbania adalah perusahaan terkemuka dalam bidang properti, memberikan layanan terbaik untuk kebutuhan Anda.';

        if (!about?.description) return fallback;

        return about.description.replace(/<\/?[^>]+(>|$)/g, '');
    }, [about]);

    return (
        <div className="about-page">
            <Helmet>
                <title>{about ? about.title : 'Tentang Kami - Urbania'}</title>
                <meta name="description" content={seoDescription} />
                <meta property="og:image" content={aboutImage} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://mitraproperti.co.id/about" />
                <link rel="canonical" href="https://mitraproperti.co.id/about" />
            </Helmet>

            {loading ? (
                <AboutSkeleton />
            ) : (
                <section className="about-section">
                    <div className="container">
                        <div className="about-hero-card">
                            <div className="row align-items-center g-4">
                                {/* IMAGE */}
                                <div className="col-lg-6">
                                    <div className="about-image-wrap">
                                        <img
                                            src={aboutImage}
                                            alt={about?.title || 'Tentang Kami'}
                                            className="about-image"
                                            onError={(e) => {
                                                console.error('Image failed:', aboutImage);

                                                e.target.src = fallbackImage;
                                            }}
                                        />

                                        <div className="about-image-badge">
                                            <span>About Us</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-6">
                                    <div className="about-content">
                                        <h1 className="about-title">
                                            {error ? 'Error loading data' : about?.title || 'No Data Available'}
                                        </h1>

                                        {error ? (
                                            <div className="alert alert-danger mt-3">{error}</div>
                                        ) : (
                                            <div
                                                className="about-description"
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        about?.description ||
                                                        '<p>Konten tentang kami belum tersedia.</p>',
                                                }}
                                            />
                                        )}

                                        <div className="about-feature-grid">
                                            <div className="about-feature-item">
                                                <div className="about-feature-dot" />

                                                <div>
                                                    <div className="about-feature-title">Layanan Properti</div>

                                                    <div className="about-feature-text">
                                                        Jual beli, sewa, dan listing properti
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="about-feature-item">
                                                <div className="about-feature-dot" />

                                                <div>
                                                    <div className="about-feature-title">Kontraktor</div>

                                                    <div className="about-feature-text">
                                                        Desain dan pembangunan properti
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="about-cta-wrap">
                                            <a
                                                href="https://wa.me/6281234567890"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="about-cta-button"
                                            >
                                                Hubungi Kami di WhatsApp
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default About;
